import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { memo } from 'react';
import { Map } from 'immutable';

import config from '../../config';
import { getSafeLink } from '../../utils/url';
import Button from '../helpers/Button';
import { AppPage } from '../AppPage';
import { GridCard } from '../helpers/GridCard';
import { MailingSummary } from '../MailingSummary';
import { MailingCreateTable } from './MailingCreateTable';

import style from './style.module.css';

let columnDefs = config.mailingCreate.getIn(['table', 'columnDefs']);
const targetWeb = columnDefs.findKey(columnDefs => columnDefs.get('field') === 'targetWeb');
const emailVerified = columnDefs.findKey(columnDefs => columnDefs.get('field') === 'emailVerified');
const logos = columnDefs.findKey(columnDefs => columnDefs.get('field') === 'logos');

if (targetWeb) {
  columnDefs = columnDefs.update(targetWeb, value =>
    value.set('cellRenderer', params => {
      const url = getSafeLink(params.value, '#');

      return `<a href="//${url}" target="_blank">${params.value}</a>`;
    }),
  );
}

if (emailVerified) {
  columnDefs = columnDefs.update(emailVerified, value =>
    value.set('cellRenderer', params => {
      const { hasEmail, isVerifyEmail } = params.data;
      const classHasEmail = `fa ${hasEmail ? 'text-success fa-check' : 'text-danger fa-times'}`;
      const titleHasEmail = hasEmail ? 'Email' : 'No Email';
      const classIsVerifyEmail = `fa ${isVerifyEmail ? 'text-success fa-check' : 'text-danger fa-times'}`;
      const titleIsVerifyEmail = isVerifyEmail ? 'Verified' : 'Not Verified';

      return `<div class="text-center">
          <i class="pr5 ${classHasEmail}" title="${titleHasEmail}"></i>
          <i class="pl5 ${classIsVerifyEmail}" title="${titleIsVerifyEmail}"></i>
        </div>`;
    }),
  );
}

if (logos) {
  columnDefs = columnDefs.update(logos, value =>
    value.set('cellRenderer', params => {
      const { isLogo } = params.data;

      const classHasLogo = `fa ${isLogo ? 'fa-check text-success' : 'fa-times text-danger'}`;
      const titleHasLogo = isLogo ? 'Has logo' : 'Has not logo';

      return `<div class="text-center">
          <i class="${classHasLogo}" title="${titleHasLogo}"></i>
        </div>`;
    }),
  );
}

/**
 * Create new mailing page component.
 *
 * @param props {Object}.
 * @param props.mailingCreate {Immutable.Map} Mailing header and the targets table data.
 * @param props.selectionQuantity {number} How many rows "Select" picks.
 * @param props.onCreateMailing {Function} Create the mailing from the selected rows.
 * @param props.onReload {Function} Reload the table.
 */
const MailingCreateComponent = ({
  mailingCreate,
  selectionQuantity,
  onUpdateSelectedRow,
  onClickSelectionControl,
  onChangeSelectionQuantity,
  onSelectByQuantity,
  onChange,
  onGetNextPageData,
  onDoubleClickRow,
  onReload,
  onCreateMailing,
}) => {
  const toolbar = (
    <div className={style.toolbar}>
      <Button onClick={onSelectByQuantity}>
        Select
      </Button>
      <input
        aria-label="Rows to select"
        className={classNames(style.quantity, 'form-control')}
        min="0"
        onChange={onChangeSelectionQuantity}
        type="number"
        value={selectionQuantity}
      />
      <Button onClick={() => onClickSelectionControl(true)}>
        Select All
      </Button>
      <Button onClick={() => onClickSelectionControl(false)}>
        Clear Selection
      </Button>
      <Button onClick={onReload}>
        <i aria-hidden="true" className="fa fa-refresh" /> Reload
      </Button>
      <Button className="btn-primary" onClick={onCreateMailing}>
        <i aria-hidden="true" className="fa fa-plus" /> Create Mailing
      </Button>
    </div>
  );

  return (
    <AppPage overline="Mailing" title="Create New Mailing" toolbar={toolbar}>
      <MailingSummary
        header={mailingCreate.get('header')}
        isEmail={mailingCreate.getIn(['header', 'isEmail']) === true}
      />
      <GridCard className={style.grid} dense>
        <MailingCreateTable
          columnDefs={columnDefs}
          data={mailingCreate}
          onChange={onChange}
          onDoubleClickRow={onDoubleClickRow}
          onGetNextPageData={onGetNextPageData}
          onUpdateSelectedRow={onUpdateSelectedRow}
        />
      </GridCard>
    </AppPage>
  );
};

MailingCreateComponent.propTypes = {
  mailingCreate: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onCreateMailing: PropTypes.func.isRequired,
  onDoubleClickRow: PropTypes.func.isRequired,
  onGetNextPageData: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
};

export const MailingCreate = memo(MailingCreateComponent);
